import { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Download, FileText, Calendar, TrendingUp } from 'lucide-react';
import { supabase } from '../../lib/supabase';

// Função para formatar data em PT-BR
const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Função para formatar data para nome de arquivo
const formatDateForFilename = (date: Date) => {
  return date.toISOString().split('T')[0];
};

export default function Reports() {
  const [dateRange, setDateRange] = useState({
    start: '',
    end: ''
  });
  const [reportData, setReportData] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const generateReport = async () => {
    setLoading(true);
    try {
      const { data: assets } = await supabase
        .from('assets')
        .select('*');

      const { data: maintenances } = await supabase
        .from('maintenances')
        .select('*');

      const { data: history } = await supabase
        .from('asset_history')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);

      setReportData({
        assets: assets || [],
        maintenances: maintenances || [],
        history: history || []
      });
    } catch (error) {
      console.error('Erro ao gerar relatório:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    if (!reportData) return;

    const csvContent = [
      ['Código do Ativo', 'Status', 'Data de Criação'],
      ...reportData.assets.map((asset: any) => [
        asset.asset_code,
        asset.status,
        formatDate(asset.created_at)
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `relatorio-ativos-${formatDateForFilename(new Date())}.csv`;
    a.click();
  };

  return (
    <DashboardLayout>
      <div className="p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Relatórios Personalizados</h1>
          <p className="text-gray-600 mt-2">Gere relatórios detalhados sobre seus ativos</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                <FileText className="w-6 h-6 text-teal-600" />
              </div>
              <h3 className="font-semibold text-gray-900">Relatório de Ativos</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Visualize todos os ativos cadastrados com seus detalhes e status atual
            </p>
            <button
              onClick={generateReport}
              disabled={loading}
              className="w-full px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
            >
              {loading ? 'Gerando...' : 'Gerar Relatório'}
            </button>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                <Calendar className="w-6 h-6 text-amber-600" />
              </div>
              <h3 className="font-semibold text-gray-900">Manutenções</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Histórico completo de manutenções realizadas e agendadas
            </p>
            <button
              onClick={generateReport}
              disabled={loading}
              className="w-full px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
            >
              {loading ? 'Gerando...' : 'Gerar Relatório'}
            </button>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="font-semibold text-gray-900">Histórico</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Acompanhe todas as alterações e movimentações dos ativos
            </p>
            <button
              onClick={generateReport}
              disabled={loading}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
            >
              {loading ? 'Gerando...' : 'Gerar Relatório'}
            </button>
          </div>
        </div>

        {reportData && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">Dados do Relatório</h3>
              <button
                onClick={exportToCSV}
                className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
              >
                <Download className="w-5 h-5" />
                Exportar CSV
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Total de Ativos</p>
                <p className="text-3xl font-bold text-gray-900">{reportData.assets.length}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Manutenções</p>
                <p className="text-3xl font-bold text-gray-900">{reportData.maintenances.length}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Registros de Histórico</p>
                <p className="text-3xl font-bold text-gray-900">{reportData.history.length}</p>
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Ativos por Status</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-sm text-green-700 mb-1">Ativos</p>
                    <p className="text-2xl font-bold text-green-900">
                      {reportData.assets.filter((a: any) => a.status === 'active').length}
                    </p>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <p className="text-sm text-gray-700 mb-1">Inativos</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {reportData.assets.filter((a: any) => a.status === 'inactive').length}
                    </p>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <p className="text-sm text-amber-700 mb-1">Em Manutenção</p>
                    <p className="text-2xl font-bold text-amber-900">
                      {reportData.assets.filter((a: any) => a.status === 'maintenance').length}
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Últimas Atividades</h4>
                <div className="space-y-2">
                  {reportData.history.slice(0, 10).map((item: any) => (
                    <div key={item.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{item.action}</p>
                        <p className="text-xs text-gray-600">
                          {formatDate(item.created_at)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
